import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type HeadlineImageLinkData } from '@/features/cms/components/CMS/types'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { Body } from '@shipt/design-system-typography'

/**
 * This component corresponds to the `headline_image_link` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/headline_image_link
 * - **Production** - https://cms.shipt.com/content-type/pool/headline_image_link
 */
export const HeadlineImageLink = ({
  content_type_id,
  id,
  data: { heading, call_to_action, image },
}: HeadlineImageLinkData) => {
  const decoratedUrl = useUtmUrl(call_to_action?.url || '')
  try {
    return (
      <Card key={heading} {...getCMSWrapperProps({ content_type_id, id })}>
        <Image src={image.src} width={image.width} height={image.height} alt={image.alt} />

        <Caption>
          <CaptionHeader as="h3">{heading}</CaptionHeader>
          {!!call_to_action && (
            <TextButtonLink href={decoratedUrl}>{call_to_action.text}</TextButtonLink>
          )}
        </Caption>
      </Card>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Card = styled.div`
  max-width: 21.5rem;
  width: 100%;
  text-align: center;
  margin: 0 auto 2rem;

  @media ${screenSizes.tablet} {
    margin: 0 0.75rem 1.5rem;
    width: 100%;
    flex: 3 1 30%;
  }
`

const Caption = styled.div`
  max-width: 21.5rem;
  margin: 1rem auto;

  p {
    margin-bottom: 0;
  }
`

const CaptionHeader = styled(Body)`
  font-weight: 600;
  font-size: 1.5rem;

  ${Caption} & {
    font-size: 1.5rem;
    letter-spacing: 0.0156rem;
    line-height: 1.22;
    margin-bottom: 0.5rem;
  }
`
